// src/components/dashboard/classicLensBackgroundEngine.js
// Original 1.0 background: color gradient + left wash + thin lines / dots /
// constellation. Very slight pincushion lens. No grain, no thick strokes.

export function mountClassicLensBackground(screen, options = {}) {
  if (!screen) {
    return { destroy() {}, apply() {}, regenerate() {}, redraw() {}, rebindScroll() {} };
  }

  const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

  const PALETTE = {
    'icy-blue': { from: '#9bc6d4', to: '#8bd0e6', base: '#8fc4d6' },
    'soft-mint': { from: '#9ed9c7', to: '#8dd5b8', base: '#8fd0bd' },
    'soft-lavender': { from: '#c4b5e0', to: '#b8a5d8', base: '#b9aad4' },
    'soft-peach': { from: '#e8c4a8', to: '#e0b899', base: '#e0bc9c' },
    'soft-sage': { from: '#a8c4b0', to: '#99b8a5', base: '#9cbcab' },
    'soft-sky': { from: '#7ec8e3', to: '#6bbddb', base: '#72c0db' },
    'soft-rose': { from: '#e0b0be', to: '#d4a0b0', base: '#d6a8b4' },
    'soft-sand': { from: '#dcc9a8', to: '#d0bc98', base: '#d4c29c' },
  };

  const CFG = {
    colorKey: 'icy-blue',
    lensX: 0.12,
    lensY: 0.38,
    parallax: 0.18,
    ...options.preset,
  };

  const STARS = [
    [0.2, 0.15], [0.35, 0.25], [0.6, 0.2], [0.8, 0.35],
    [0.15, 0.45], [0.45, 0.5], [0.7, 0.6], [0.9, 0.65],
    [0.25, 0.75], [0.55, 0.8], [0.85, 0.85],
  ];
  const LINKS = [
    [0, 1], [1, 2], [2, 3], [4, 5], [5, 6], [6, 7],
    [8, 9], [9, 10], [1, 4], [2, 5], [3, 6], [5, 8],
  ];

  const baseC = document.createElement('canvas');
  const inkC = document.createElement('canvas');
  const ctx = baseC.getContext('2d');
  const itx = inkC.getContext('2d');
  let dead = false;
  let W = 0;
  let H = 0;
  let Hb = 0;
  let dpr = 1;
  let firstDraw = true;

  function hexToRgb(hex) {
    const h = String(hex || '#88aacc').replace('#', '');
    return [1, 3, 5].map((i) => parseInt(h.slice(i - 1, i + 1), 16));
  }

  function paintSurface(width, height) {
    const pal = PALETTE[CFG.colorKey] || PALETTE['icy-blue'];
    const [fr, fg, fb] = hexToRgb(pal.from);
    const [tr, tg, tb] = hexToRgb(pal.to);

    const g = ctx.createLinearGradient(0, 0, width, 0);
    g.addColorStop(0, `rgb(${fr},${fg},${fb})`);
    g.addColorStop(1, `rgb(${tr},${tg},${tb})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, width, height);

    const wash = ctx.createLinearGradient(0, 0, width, 0);
    wash.addColorStop(0, 'rgba(0,0,0,0.72)');
    wash.addColorStop(0.42, 'rgba(0,0,0,0.22)');
    wash.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = wash;
    ctx.fillRect(0, 0, width, height);

    const topRight = ctx.createRadialGradient(width, 0, 0, width, 0, 192);
    topRight.addColorStop(0, 'rgba(255,255,255,0.1)');
    topRight.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = topRight;
    ctx.fillRect(width - 192, 0, 192, 192);

    const botLeft = ctx.createRadialGradient(0, height, 0, 0, height, 192);
    botLeft.addColorStop(0, 'rgba(255,255,255,0.1)');
    botLeft.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = botLeft;
    ctx.fillRect(0, height - 192, 192, 192);
  }

  function paintInk(width, height) {
    itx.clearRect(0, 0, width, height);
    const draw = itx;

    draw.strokeStyle = 'rgba(255,255,255,0.05)';
    draw.lineWidth = 1;
    draw.beginPath();
    draw.arc(width * 0.25, height * 0.25, 64, 0, Math.PI * 2);
    draw.stroke();
    draw.save();
    draw.translate(width * 0.75, height * 0.33);
    draw.rotate(Math.PI / 4);
    draw.strokeRect(-48, -48, 96, 96);
    draw.restore();
    draw.beginPath();
    draw.arc(width * 0.33, height * 0.75, 80, 0, Math.PI * 2);
    draw.stroke();

    const ribbons = [
      { y: 0.25, rot: -12, a: 0.3, h: 2 },
      { y: 0.5, rot: 6, a: 0.25, h: 2 },
      { y: 0.75, rot: -3, a: 0.3, h: 2 },
      { y: 0.33, rot: 12, a: 0.2, h: 1 },
      { y: 0.66, rot: -6, a: 0.15, h: 1 },
    ];
    for (const rib of ribbons) {
      draw.save();
      draw.translate(width / 2, height * rib.y);
      draw.rotate((rib.rot * Math.PI) / 180);
      const lg = draw.createLinearGradient(-width, 0, width, 0);
      lg.addColorStop(0, 'rgba(255,255,255,0)');
      lg.addColorStop(0.5, `rgba(255,255,255,${rib.a})`);
      lg.addColorStop(1, 'rgba(255,255,255,0)');
      draw.fillStyle = lg;
      draw.fillRect(-width, -rib.h / 2, width * 2, rib.h);
      draw.restore();
    }

    for (const x of [0.25, 0.75]) {
      const vg = draw.createLinearGradient(0, 0, 0, height);
      vg.addColorStop(0, 'rgba(255,255,255,0)');
      vg.addColorStop(0.5, x === 0.25 ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.15)');
      vg.addColorStop(1, 'rgba(255,255,255,0)');
      draw.fillStyle = vg;
      draw.fillRect(width * x, 0, 1, height);
    }

    draw.lineCap = 'round';
    draw.lineWidth = 0.75;
    const linkA = [0.25, 0.2, 0.25, 0.15, 0.2, 0.25, 0.15, 0.2, 0.15, 0.18, 0.15, 0.1];
    LINKS.forEach((pair, i) => {
      const [a, b] = pair;
      draw.strokeStyle = `rgba(255,255,255,${linkA[i] || 0.18})`;
      draw.beginPath();
      draw.moveTo(width * STARS[a][0], height * STARS[a][1]);
      draw.lineTo(width * STARS[b][0], height * STARS[b][1]);
      draw.stroke();
    });

    const sizes = [2, 1.5, 2.5, 1, 1.5, 2, 1.5, 2, 1, 1.5, 1.5];
    const alphas = [0.6, 0.5, 0.7, 0.6, 0.6, 0.5, 0.5, 0.6, 0.6, 0.7, 0.5];
    for (let i = 0; i < STARS.length; i++) {
      draw.fillStyle = `rgba(255,255,255,${alphas[i]})`;
      draw.beginPath();
      draw.arc(width * STARS[i][0], height * STARS[i][1], sizes[i], 0, Math.PI * 2);
      draw.fill();
    }
  }

  let gl = null;
  let lens = null;

  function initGL() {
    gl = screen.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false });
    if (!gl) return null;
    const compile = (type, src) => {
      const sh = gl.createShader(type);
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      return sh;
    };
    const prog = gl.createProgram();
    gl.attachShader(
      prog,
      compile(
        gl.VERTEX_SHADER,
        'attribute vec2 a; varying vec2 v; void main(){ v = a * 0.5 + 0.5; gl_Position = vec4(a, 0.0, 1.0); }'
      )
    );
    gl.attachShader(
      prog,
      compile(
        gl.FRAGMENT_SHADER,
        `
      precision highp float;
      uniform sampler2D base, ink;
      uniform vec2 k, win, off, board;
      uniform float time;
      varying vec2 v;
      float hash(vec2 i){
        return fract(sin(dot(i, vec2(127.1, 311.7))) * 43758.5453);
      }
      void main(){
        vec2 p = v * 2.0 - 1.0;
        float r2 = p.x * p.x * 0.4 + p.y * p.y;
        vec2 s = p / (1.0 + k * r2);
        vec2 uv = (s * 0.5 + 0.5) * win + off;
        vec3 c = texture2D(base, uv).rgb;
        vec4 line = texture2D(ink, uv);
        float pulse = 0.9 + 0.1 * sin(time * 0.085);
        c += line.rgb * line.a * pulse;

        vec2 pos = uv * board;
        vec2 cell = floor((pos - 20.0) / 40.0);
        vec2 center = 20.0 + cell * 40.0;
        float d = length(pos - center);
        float h = hash(cell);
        float period = 24.0 + h * 32.0;
        float t = fract(time / period + h);
        float wave = 0.5 + 0.5 * sin(6.28318 * t);
        float glow = pow(max(wave, 0.0), 14.0);
        float radius = mix(1.75, 2.25, wave);
        if (d < radius && pos.x >= 0.0 && pos.y >= 0.0 && pos.x <= board.x && pos.y <= board.y) {
          float alpha = mix(0.2, 0.38, wave) + glow * 0.12;
          c += vec3(alpha);
        }
        gl_FragColor = vec4(c, 1.0);
      }`
      )
    );
    gl.linkProgram(prog);
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const makeTex = (unit) => {
      const tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      return tex;
    };
    const texBase = makeTex(0);
    const texInk = makeTex(1);
    gl.uniform1i(gl.getUniformLocation(prog, 'base'), 0);
    gl.uniform1i(gl.getUniformLocation(prog, 'ink'), 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    const u = {};
    for (const n of ['k', 'win', 'off', 'board', 'time']) u[n] = gl.getUniformLocation(prog, n);
    const up = (unit, tex, src) => {
      gl.activeTexture(gl.TEXTURE0 + unit);
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, src);
    };
    return {
      maxTex: gl.getParameter(gl.MAX_TEXTURE_SIZE),
      uploadBase() { up(0, texBase, baseC); },
      uploadInk() { up(1, texInk, inkC); },
      draw(viewW, viewH, boardH, pixelRatio, bgOff, now, boardW) {
        const pw = Math.round(viewW * pixelRatio);
        const ph = Math.round(viewH * pixelRatio);
        if (screen.width !== pw || screen.height !== ph) {
          screen.width = pw;
          screen.height = ph;
        }
        gl.viewport(0, 0, pw, ph);
        gl.uniform2f(u.k, CFG.lensX, CFG.lensY);
        gl.uniform2f(u.win, 1, viewH / boardH);
        gl.uniform2f(u.off, 0, 1 - (bgOff + viewH) / boardH);
        gl.uniform2f(u.board, boardW, boardH);
        gl.uniform1f(u.time, now);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      },
    };
  }

  lens = initGL();
  const ctx2d = lens ? null : screen.getContext('2d');

  const scrollEl = () =>
    typeof options.getScrollElement === 'function' ? options.getScrollElement() : null;

  const bgOffset = () => {
    const travel = Math.max(0, Hb - H);
    const el = scrollEl();
    const max = el
      ? Math.max(0, el.scrollHeight - el.clientHeight)
      : Math.max(0, document.documentElement.scrollHeight - H);
    const top = el ? el.scrollTop : window.scrollY || 0;
    const p = max > 0 ? top / max : 0;
    return clamp(p * travel, 0, travel);
  };

  let clock = 0;

  function redraw() {
    if (dead) return;
    if (lens) lens.draw(W, H, Hb, dpr, bgOffset(), clock, W);
    else if (ctx2d) {
      screen.width = W;
      screen.height = H;
      const sy = bgOffset() * (baseC.height / Hb);
      const sh = baseC.height * (H / Hb);
      ctx2d.drawImage(baseC, 0, sy, baseC.width, sh, 0, 0, W, H);
    }
    if (firstDraw) {
      firstDraw = false;
      screen.style.opacity = '1';
    }
  }

  function regenerate() {
    if (dead) return;
    W = window.innerWidth;
    H = window.innerHeight;
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pal = PALETTE[CFG.colorKey] || PALETTE['icy-blue'];
    if (screen.parentElement) screen.parentElement.style.background = pal.base;
    Hb = Math.round(H * (1 + Math.min(1, CFG.parallax * 1.4)));
    let sc = dpr;
    if (lens) sc = Math.min(sc, lens.maxTex / Math.max(W, Hb), Math.sqrt((8e6) / (W * Hb)));
    else sc = Math.min(dpr, 1.5);

    const bw = Math.max(1, Math.round(W * sc));
    const bh = Math.max(1, Math.round(Hb * sc));
    baseC.width = bw;
    baseC.height = bh;
    inkC.width = bw;
    inkC.height = bh;
    ctx.setTransform(sc, 0, 0, sc, 0, 0);
    itx.setTransform(sc, 0, 0, sc, 0, 0);
    paintSurface(W, Hb);
    paintInk(W, Hb);

    if (lens) {
      lens.uploadBase();
      lens.uploadInk();
      baseC.width = 0;
      baseC.height = 0;
      inkC.width = 0;
      inkC.height = 0;
    }
    redraw();
  }

  let scrollTarget = null;
  const onScroll = () => redraw();
  const onResize = () => regenerate();
  let raf = 0;

  function tick(now) {
    if (dead) return;
    clock = now * 0.001;
    redraw();
    raf = window.requestAnimationFrame(tick);
  }

  function bindScroll() {
    if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
    scrollTarget = scrollEl() || window;
    scrollTarget.addEventListener('scroll', onScroll, { passive: true });
  }

  window.addEventListener('resize', onResize);
  bindScroll();
  regenerate();
  raf = window.requestAnimationFrame(tick);

  return {
    destroy() {
      dead = true;
      window.cancelAnimationFrame(raf);
      window.removeEventListener('resize', onResize);
      if (scrollTarget) scrollTarget.removeEventListener('scroll', onScroll);
    },
    apply(preset) {
      Object.assign(CFG, preset || {});
      regenerate();
    },
    regenerate,
    redraw,
    rebindScroll() {
      bindScroll();
      redraw();
    },
  };
}
